import { Fragment, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { MdDelete, MdEdit } from "react-icons/md";
import Swal from "sweetalert2";
import {
  useAjouterRegleMutation,
  useModifierRegleMutation,
  useReglesQuery,
  useSupprimerRegleMutation,
} from "../../../../../utils/api/retrait/retrait.api";
import {
  RegleFormData,
  RegleRetrait,
} from "../../../../../utils/api/retrait/regle.type";
import { CANAUX_VERSEMENT } from "../../../../../utils/api/retrait/retrait.type";
import { COLORS } from "../../../../../utils/constants";

// Un retrait est soit du mobile money, soit un virement : il n'y a pas de
// troisieme cas, donc pas de joker. Deux regles sans pays suffisent a tout
// couvrir.
const MODES = [
  { value: "mobile_money", label: "Mobile money" },
  { value: "bancaire", label: "Virement bancaire" },
  { value: "carte", label: "Virement par carte" },
];

const PAYS = Object.values(CANAUX_VERSEMENT);

const VIDE: RegleFormData = {
  libelle: "",
  mode: "mobile_money",
  pays: null,
  canal: null,
  pourcentage: 0,
  frais_fixe: 0,
  minimum: 0,
  plafond: 0,
  actif: true,
};

const nombre = (v: any) => Number(v ?? 0) || 0;

// Même règle de priorité que le serveur : canal (3) > pays (2) > mode (1).
// L'admin doit voir ses règles dans l'ordre où elles s'appliqueront.
const precision = (r: RegleRetrait) =>
  (r.canal ? 3 : 0) + (r.pays ? 2 : 0) + (r.mode ? 1 : 0);

function Portee({
  regle,
  masquerPays,
}: {
  regle: RegleRetrait;
  masquerPays?: boolean;
}) {
  const puces: string[] = [];
  puces.push(MODES.find((m) => m.value === regle.mode)?.label || "Moyen non défini");
  // Dans un groupe pays, répéter le pays à chaque ligne n'apprend rien.
  if (!masquerPays) puces.push(regle.pays ? CANAUX_VERSEMENT[regle.pays]?.nom || regle.pays : "Tous les pays");
  if (regle.canal) {
    const canal = PAYS.flatMap((p) => p.canaux).find((c) => c.slug === regle.canal);
    puces.push(canal?.label || regle.canal);
  }
  return (
    <div className="d-flex flex-wrap gap-1">
      {puces.map((p) => (
        <span
          key={p}
          style={{
            fontSize: 11,
            padding: "2px 8px",
            borderRadius: 20,
            background: "rgba(2,24,73,0.07)",
            color: COLORS.blue,
            whiteSpace: "nowrap",
          }}
        >
          {p}
        </span>
      ))}
    </div>
  );
}

export default function BaremesRetrait() {
  const { data: regles = [], isLoading } = useReglesQuery();
  const [ajouter, { isLoading: ajout }] = useAjouterRegleMutation();
  const [modifier, { isLoading: edition }] = useModifierRegleMutation();
  const [supprimer] = useSupprimerRegleMutation();

  const [form, setForm] = useState<RegleFormData | null>(null);
  const [slugEdite, setSlugEdite] = useState<string | null>(null);

  // Une section par pays, précédée des règles générales : l'admin doit
  // pouvoir constater d'un coup d'œil qu'aucun pays n'est laissé sans barème.
  const sections = useMemo(() => {
    const par = (filtre: (r: RegleRetrait) => boolean) =>
      [...regles].filter(filtre).sort((a, b) => precision(b) - precision(a));
    return [
      {
        code: null as string | null,
        titre: "Règles générales",
        aide: "S'appliquent quand aucune règle du pays ne correspond.",
        lignes: par((r) => !r.pays),
      },
      ...PAYS.map((pays) => ({
        code: pays.code,
        titre: pays.nom,
        aide: "",
        lignes: par((r) => r.pays === pays.code),
      })),
    ];
  }, [regles]);

  const canauxDuPays =
    form?.pays && CANAUX_VERSEMENT[form.pays] ? CANAUX_VERSEMENT[form.pays].canaux : [];

  const change = (champ: keyof RegleFormData, valeur: any) =>
    setForm((old) => (old ? { ...old, [champ]: valeur } : old));

  const ouvrir = (regle?: RegleRetrait, prefill?: Partial<RegleFormData>) => {
    if (regle) {
      setSlugEdite(regle.slug);
      setForm({
        libelle: regle.libelle || "",
        mode: regle.mode,
        pays: regle.pays,
        canal: regle.canal,
        pourcentage: nombre(regle.pourcentage),
        frais_fixe: nombre(regle.frais_fixe),
        minimum: nombre(regle.minimum),
        plafond: nombre(regle.plafond),
        actif: regle.actif,
      });
    } else {
      setSlugEdite(null);
      setForm({ ...VIDE, ...prefill });
    }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    if (!form) return;
    const payload: RegleFormData = {
      ...form,
      libelle: form.libelle || null,
      mode: form.mode || null,
      pays: form.pays || null,
      // Un canal sans pays n'a pas de sens : le slug est déjà propre au pays.
      canal: form.pays ? form.canal || null : null,
      pourcentage: nombre(form.pourcentage),
      frais_fixe: nombre(form.frais_fixe),
      minimum: nombre(form.minimum),
      plafond: nombre(form.plafond),
    };
    const res: any = slugEdite
      ? await modifier({ slug: slugEdite, ...payload })
      : await ajouter(payload);
    if (res?.error) {
      return Swal.fire({
        icon: "error",
        title: "Règle refusée",
        text: res.error?.data?.message || "Vérifiez les valeurs saisies.",
        confirmButtonColor: COLORS.blue,
      });
    }
    setForm(null);
    setSlugEdite(null);
    Swal.fire({
      icon: "success",
      title: slugEdite ? "Règle modifiée" : "Règle ajoutée",
      iconColor: COLORS.green,
      showConfirmButton: false,
      timer: 1400,
    });
  };

  const effacer = async (regle: RegleRetrait) => {
    const choix = await Swal.fire({
      icon: "warning",
      title: "Supprimer cette règle ?",
      text: "Les retraits concernés repasseront sur la règle suivante, ou sur le barème global.",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
      confirmButtonColor: COLORS.red,
      cancelButtonColor: COLORS.blue,
    });
    if (!choix.isConfirmed) return;
    await supprimer(regle.slug);
    if (slugEdite === regle.slug) {
      setForm(null);
      setSlugEdite(null);
    }
  };

  // Aperçu : la commission est déduite du montant demandé, le porteur
  // reçoit le net. C'est le calcul du serveur, reproduit à l'identique.
  const apercu = useMemo(() => {
    if (!form) return null;
    const base = Math.max(nombre(form.minimum), 10000);
    const commission = (base * nombre(form.pourcentage)) / 100 + nombre(form.frais_fixe);
    return { base, commission, net: Math.max(base - commission, 0) };
  }, [form]);

  if (isLoading) return <h4>Chargement...</h4>;

  return (
    <div>
      {MODES.filter(
        (m) => !sections[0].lignes.some((r) => r.mode === m.value && !r.canal)
      ).map((m) => (
        <div key={m.value} className="alert alert-warning" style={{ fontSize: 13 }}>
          Aucune règle générale pour « {m.label} » : un retrait par ce moyen
          dans un pays sans règle propre ne trouverait aucun barème. Créez une
          règle « {m.label} » sans pays.
        </div>
      ))}

      {/* Masque pendant l'edition : deux formulaires concurrents n'ont pas
          de sens, et on ne sait plus laquelle des deux regles on enregistre. */}
      {!form && (
        <div className="flex-r mb-3">
          <button
            type="button"
            className="admin-submit-btn d-inline-flex align-items-center"
            style={{ gap: 8 }}
            onClick={() => ouvrir()}
          >
            <FiPlus size={18} />
            <span>Nouvelle règle</span>
          </button>
        </div>
      )}

      {form && (
        <form
          onSubmit={enregistrer}
          className="p-3 mb-4"
          style={{ border: `1px solid ${COLORS.blue}`, borderRadius: 10 }}
        >
          <h5
            style={{
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: 15,
              color: COLORS.blue,
            }}
          >
            {slugEdite ? "Modifier la règle" : "Nouvelle règle"}
          </h5>

          <div className="row">
            <div className="col-md-6 my-2">
              <label className="form-send-point-label">Nom de la règle</label>
              <input
                className="form-control-majjal form-control form-control-lg"
                placeholder="Ex. Wave Sénégal"
                value={form.libelle || ""}
                onChange={(e) => change("libelle", e.target.value)}
              />
            </div>

            <div className="col-md-6 my-2">
              <label className="form-send-point-label">Moyen de retrait</label>
              <select
                className="form-control-majjal form-control form-control-lg"
                value={form.mode || ""}
                onChange={(e) => {
                  const mode = e.target.value || null;
                  setForm((old) =>
                    old
                      ? {
                          ...old,
                          mode: mode as any,
                          // Un operateur n'existe qu'en mobile money : hors de
                          // ce mode, le canal n'a plus de sens.
                          canal: mode === "mobile_money" ? old.canal : null,
                        }
                      : old
                  );
                }}
              >
                {MODES.map((m) => (
                  <option
                    key={m.value}
                    value={m.value}
                    disabled={!!form.canal && m.value !== "mobile_money"}
                  >
                    {m.label}
                  </option>
                ))}
              </select>
              {form.canal && (
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4 }}>
                  Un opérateur se paie forcément en mobile money.
                </div>
              )}
            </div>

            <div className="col-md-6 my-2">
              <label className="form-send-point-label">Pays</label>
              <select
                className="form-control-majjal form-control form-control-lg"
                value={form.pays || ""}
                onChange={(e) =>
                  setForm((old) =>
                    old ? { ...old, pays: e.target.value || null, canal: null } : old
                  )
                }
              >
                <option value="">Tous les pays</option>
                {PAYS.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 my-2">
              <label className="form-send-point-label">Opérateur</label>
              <select
                className="form-control-majjal form-control form-control-lg"
                value={form.canal || ""}
                disabled={!form.pays || form.mode !== "mobile_money"}
                onChange={(e) => {
                  const canal = e.target.value || null;
                  // Choisir un operateur fixe le moyen : on ne laisse pas
                  // enregistrer « Orange Money, tous les moyens ».
                  setForm((old) =>
                    old
                      ? { ...old, canal, mode: canal ? "mobile_money" : old.mode }
                      : old
                  );
                }}
              >
                <option value="">Tous les opérateurs</option>
                {canauxDuPays.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                  </option>
                ))}
              </select>
              {!form.pays && (
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4 }}>
                  Choisissez d'abord un pays.
                </div>
              )}
            </div>

            <div className="col-md-3 my-2">
              <label className="form-send-point-label">Commission (%)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                className="form-control-majjal form-control form-control-lg"
                value={form.pourcentage as any}
                onChange={(e) => change("pourcentage", e.target.value)}
              />
            </div>
            <div className="col-md-3 my-2">
              <label className="form-send-point-label">Frais fixes (FCFA)</label>
              <input
                type="number"
                min={0}
                className="form-control-majjal form-control form-control-lg"
                value={form.frais_fixe as any}
                onChange={(e) => change("frais_fixe", e.target.value)}
              />
            </div>
            <div className="col-md-3 my-2">
              <label className="form-send-point-label">Minimum (FCFA)</label>
              <input
                type="number"
                min={0}
                className="form-control-majjal form-control form-control-lg"
                value={form.minimum as any}
                onChange={(e) => change("minimum", e.target.value)}
              />
            </div>
            <div className="col-md-3 my-2">
              <label className="form-send-point-label">Plafond (FCFA)</label>
              <input
                type="number"
                min={0}
                className="form-control-majjal form-control form-control-lg"
                value={form.plafond as any}
                onChange={(e) => change("plafond", e.target.value)}
              />
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4 }}>
                0 = aucun plafond.
              </div>
            </div>

            <div className="col-md-6 my-2">
              <label className="form-send-point-label">État</label>
              <select
                className="form-control-majjal form-control form-control-lg"
                value={form.actif ? "true" : "false"}
                onChange={(e) => change("actif", e.target.value === "true")}
              >
                <option value="true">Active</option>
                <option value="false">Désactivée</option>
              </select>
            </div>
          </div>

          {apercu && (
            <div
              className="mt-2 p-2"
              style={{ background: "rgba(2,24,73,0.05)", borderRadius: 8, fontSize: 13 }}
            >
              Sur une demande de <b>{apercu.base.toLocaleString("fr-FR")} FCFA</b>, la
              commission serait de{" "}
              <b>{apercu.commission.toLocaleString("fr-FR")} FCFA</b> et le porteur
              recevrait <b>{apercu.net.toLocaleString("fr-FR")} FCFA</b>.
            </div>
          )}

          <div className="flex-r gap-2 mt-3">
            <button
              type="button"
              className="btn"
              style={{ fontSize: 13, color: "rgba(0,0,0,0.6)" }}
              onClick={() => {
                setForm(null);
                setSlugEdite(null);
              }}
            >
              Annuler
            </button>
            <button type="submit" disabled={ajout || edition} className="admin-submit-btn">
              {ajout || edition ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      )}

      <div className="table-responsive">
        <table className="table table-striped" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ fontFamily: "Poppins", fontSize: 12, color: COLORS.blue }}>
              <th>Règle</th>
              <th>Portée</th>
              <th className="text-end">Commission</th>
              <th className="text-end">Minimum</th>
              <th className="text-end">Plafond</th>
              <th>État</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <Fragment key={section.code || "general"}>
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      background: "rgba(2,24,73,0.05)",
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      fontSize: 13,
                      color: COLORS.blue,
                    }}
                  >
                    {section.titre}
                    {section.aide && (
                      <span
                        style={{
                          fontWeight: 400,
                          fontSize: 11,
                          color: "rgba(0,0,0,0.55)",
                          marginLeft: 8,
                        }}
                      >
                        {section.aide}
                      </span>
                    )}
                  </td>
                </tr>

                {section.lignes.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                      Aucune règle propre à ce pays : les règles générales
                      s'appliquent.
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm p-0"
                        style={{ fontSize: 12, color: COLORS.blue }}
                        onClick={() =>
                          ouvrir(undefined, {
                            pays: section.code,
                            mode: "mobile_money",
                            libelle: `Mobile money ${section.titre}`,
                          })
                        }
                      >
                        + Ajouter
                      </button>
                    </td>
                  </tr>
                )}

                {section.lignes.map((r) => (
                  <tr key={r.slug} style={{ opacity: r.actif ? 1 : 0.5 }}>
                    <td>{r.libelle || "—"}</td>
                    <td>
                      <Portee regle={r} masquerPays={!!section.code} />
                    </td>
                    <td className="text-end">
                      {nombre(r.pourcentage)} %
                      {nombre(r.frais_fixe) > 0 &&
                        ` + ${nombre(r.frais_fixe).toLocaleString("fr-FR")}`}
                    </td>
                    <td className="text-end">
                      {nombre(r.minimum).toLocaleString("fr-FR")}
                    </td>
                    <td className="text-end">
                      {nombre(r.plafond) > 0
                        ? nombre(r.plafond).toLocaleString("fr-FR")
                        : "—"}
                    </td>
                    <td>{r.actif ? "Active" : "Désactivée"}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm"
                        title="Modifier"
                        onClick={() => ouvrir(r)}
                      >
                        <MdEdit color={COLORS.blue} size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        title="Supprimer"
                        onClick={() => effacer(r)}
                      >
                        <MdDelete color={COLORS.red} size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
